import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import {  Observable } from 'rxjs';
import { ComponentBase } from '../components/base/common-base';
import { BUSINESS_TYPE, CITY, KNOW_US } from '../model/login';
import { RESPONCE } from '../model/responce';
import { COUNTRY } from '../model/signUp.model';
import { ApiService } from './api.service';
import { REGISTRATION_PHASES_RESPONSE } from '../model/step-phases.model';

@Injectable({
  providedIn: 'root',
})
export class SharedService extends ComponentBase {
  /** Cache of audio instances by file path to reduce repeated loading delay. */
  private readonly audioCache = new Map<string, HTMLAudioElement>();
  /** Whether the client currently has a network connection (e.g. from navigator.onLine or app logic). */
  public hasNetworkConnection: boolean = false;
  /** Backing field for registrationCycleLogId. */
  private _registrationCycleLogId: number | undefined;
  /** Backing field for isMobileNumberVerified. */
  private _isMobileNumberVerified: boolean | undefined;
  /** Backing field for doneUserDataBySkip. */
  private _doneUserDataBySkip: boolean | undefined;
  /** Backing field for userData. */
  private _userData: REGISTRATION_PHASES_RESPONSE['One']['OneData'] | undefined;
  /** Backing field for doneBasicDetailsBySkip. */
  private _doneBasicDetailsBySkip: boolean | undefined;
  /** Backing field for basicDetails. */
  private _basicDetails: REGISTRATION_PHASES_RESPONSE['Two']['TwoData'] | undefined;
  /** Backing field for doneBusinessDetailsBySkip. */
  private _doneBusinessDetailsBySkip: boolean | undefined;
  /** Backing field for businessDetails. */
  private _businessDetails: REGISTRATION_PHASES_RESPONSE['Three']['ThreeData'] | undefined;
  /** Backing field for doneWorkHoursDetailsBySkip. */
  private _doneWorkHoursDetailsBySkip: boolean | undefined;
  /** Backing field for workHoursDetails. */
  private _workHoursDetails: { WorkingHours?: { WeekDay: number; StartTime: string; FinishTime: string; IsDayOff?: boolean }[] } | undefined;
  /** Backing field for currency returned from response (nullable). */
  private _currency: unknown | null = null;

  // --- Getters / Setters for getRegisteredPhases result (set on success) ---

  /** Identifier of the current registration cycle log (from getRegisteredPhases Result). */
  get registrationCycleLogId(): number | undefined {
    return this._registrationCycleLogId;
  }
  /** Sets the registration cycle log id (from getRegisteredPhases Result). */
  set registrationCycleLogId(value: number | undefined) {
    this._registrationCycleLogId = value;
  }

  /** Whether the user's mobile number has been verified (from getRegisteredPhases Result). */
  get isMobileNumberVerified(): boolean | undefined {
    return this._isMobileNumberVerified;
  }
  /** Sets whether the user's mobile number has been verified. */
  set isMobileNumberVerified(value: boolean | undefined) {
    this._isMobileNumberVerified = value;
  }

  /** Whether phase One (user/sign-up) was completed by skipping (Result.One.DoneBySkip). */
  get doneUserDataBySkip(): boolean | undefined {
    return this._doneUserDataBySkip;
  }
  /** Sets whether phase One was completed by skipping (Result.One.DoneBySkip). */
  set doneUserDataBySkip(value: boolean | undefined) {
    this._doneUserDataBySkip = value;
  }

  /** Phase One user data: FirstName, LastName, Email, MobileNumber, CountryId, etc. (Result.One.OneData). */
  get userData(): REGISTRATION_PHASES_RESPONSE['One']['OneData'] | undefined {
    return this._userData;
  }
  /** Sets phase One user data (Result.One.OneData). */
  set userData(value: REGISTRATION_PHASES_RESPONSE['One']['OneData'] | undefined) {
    this._userData = value;
  }

  /** Whether phase Two (basic details) was completed by skipping (Result.Two.DoneBySkip). */
  get doneBasicDetailsBySkip(): boolean | undefined {
    return this._doneBasicDetailsBySkip;
  }
  /** Sets whether phase Two was completed by skipping (Result.Two.DoneBySkip). */
  set doneBasicDetailsBySkip(value: boolean | undefined) {
    this._doneBasicDetailsBySkip = value;
  }

  /** Phase Two basic details: BusinessName, BusinessTypes, CountryId, CityId, etc. (Result.Two.TwoData). */
  get basicDetails(): REGISTRATION_PHASES_RESPONSE['Two']['TwoData'] | undefined {
    return this._basicDetails;
  }
  /** Sets phase Two basic details (Result.Two.TwoData). */
  set basicDetails(value: REGISTRATION_PHASES_RESPONSE['Two']['TwoData'] | undefined) {
    this._basicDetails = value;
  }

  /** Whether phase Three (business details) was completed by skipping (Result.Three.DoneBySkip). */
  get doneBusinessDetailsBySkip(): boolean | undefined {
    return this._doneBusinessDetailsBySkip;
  }
  /** Sets whether phase Three was completed by skipping (Result.Three.DoneBySkip). */
  set doneBusinessDetailsBySkip(value: boolean | undefined) {
    this._doneBusinessDetailsBySkip = value;
  }

  /** Phase Three business details: logo, phone, position, branches, employees, etc. (Result.Three.ThreeData). */
  get businessDetails(): REGISTRATION_PHASES_RESPONSE['Three']['ThreeData'] | undefined {
    return this._businessDetails;
  }
  /** Sets phase Three business details (Result.Three.ThreeData). */
  set businessDetails(value: REGISTRATION_PHASES_RESPONSE['Three']['ThreeData'] | undefined) {
    this._businessDetails = value;
  }

  /** Whether phase Four (work hours) was completed by skipping (Result.Four.DoneBySkip). */
  get doneWorkHoursDetailsBySkip(): boolean | undefined {
    return this._doneWorkHoursDetailsBySkip;
  }
  /** Sets whether phase Four was completed by skipping (Result.Four.DoneBySkip). */
  set doneWorkHoursDetailsBySkip(value: boolean | undefined) {
    this._doneWorkHoursDetailsBySkip = value;
  }

  /** Phase Four work hours data: WorkingHours array per week day (Result.Four.FourData). */
  get workHoursDetails(): { WorkingHours?: { WeekDay: number; StartTime: string; FinishTime: string; IsDayOff?: boolean }[] } | undefined {
    return this._workHoursDetails;
  }
  /** Sets phase Four work hours data (Result.Four.FourData). */
  set workHoursDetails(value: { WorkingHours?: { WeekDay: number; StartTime: string; FinishTime: string; IsDayOff?: boolean }[] } | undefined) {
    this._workHoursDetails = value;
  }

  /** Currency from the API response. Defaults to null when missing. */
  get currency(): unknown | null {
    return this._currency;
  }
  /** Sets currency from API response; nullable when not provided. */
  set currency(value: unknown | null) {
    this._currency = value;
  }

  /** Fills all registered-phases getters from a successful getRegisteredPhases Result. Call this in app.component (or elsewhere) when the response succeeds. */
  setRegisteredPhasesResult(result: REGISTRATION_PHASES_RESPONSE & { IsMobileNumberVerified?: boolean }): void {
    if (!result) return;
    this._registrationCycleLogId = result.RegistrationCycleLogId;
    this._isMobileNumberVerified = result.IsMobileNumberVerified;
    this._doneUserDataBySkip = result.One?.DoneBySkip;
    this._userData = result.One?.OneData;
    this._doneBasicDetailsBySkip = result.Two?.DoneBySkip;
    this._basicDetails = result.Two?.TwoData;
    this._doneBusinessDetailsBySkip = result.Three?.DoneBySkip;
    this._businessDetails = result.Three?.ThreeData;
    this._doneWorkHoursDetailsBySkip = result.Four?.DoneBySkip;
    this._workHoursDetails = (result as { Four?: { FourData?: typeof this._workHoursDetails } }).Four?.FourData;
    this._currency = (result as { CurrencyCode?: unknown }).CurrencyCode ?? null;
  }

  constructor(
    private readonly toastr: ToastrService,
    private readonly spinner: NgxSpinnerService,
    private readonly apiService: ApiService,
  ) {
    super();
  }

  /** Loads countries for phase-one signup (includes current country when requested). */
  getCountries(): Observable<RESPONCE<COUNTRY[]>> {
    return this.apiService.get<RESPONCE<COUNTRY[]>>(
      'RegistrationCyclePhaseOne/GetCountries',
      { WithCurrentOne: true }
    );
  }

  /** Loads business types available for the given country (phase two). */
  getBusinessType(countryId: number): Observable<RESPONCE<BUSINESS_TYPE[]>> {
    return this.apiService.get<RESPONCE<BUSINESS_TYPE[]>>(
      'RegistrationCyclePhaseTwo/GetBusinessTypes?',
      { countryId: countryId }
    );
  }

  /** Loads cities for the selected country (phase two). */
  getCities(countryId: number): Observable<RESPONCE<CITY[]>> {
    return this.apiService.get<RESPONCE<CITY[]>>(
      'RegistrationCyclePhaseTwo/GetCities',
      { countryId: countryId }
    );
  }

  /** Loads “how did you hear about us” options for phase two. */
  getKnowUs(): Observable<RESPONCE<KNOW_US[]>> {
    return this.apiService.get<RESPONCE<KNOW_US[]>>(
      'RegistrationCyclePhaseTwo/GetKnowUsWays',
      ''
    );
  }

  /** Persists a string value in localStorage under the given key. */
  setLocalStorage(type: string, value: string): void {
    localStorage.setItem(type, value);
  }

  /** Reads a string from localStorage for the given key (asserts present). */
  getLocalStorage(type: string): string {
    return localStorage.getItem(type)!;
  }

  /** Sets a browser cookie with the given name, value, and optional expiry in minutes (default 1). */
  setCookie(
    name: string,
    value: string,
    minutes: number = 1
  ): void {
    const date = new Date();
    date.setTime(date.getTime() + (minutes * 60 * 1000));
  
    const expires = "expires=" + date.toUTCString();
  
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  /** Returns the value of the cookie with the given name, or null if not found. */
  getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
  
    for (let c of ca) {
      while (c.charAt(0) === ' ') c = c.substring(1);
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length);
      }
    }
    return null;
  }

  /** Removes the cookie with the given name by setting its expiry to the past. */
  clearCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  /**
   * Maps an API status code to a toast and success flag: hides the spinner, shows success or error, and returns whether the call should be treated as successful.
   */
  checkResponseStatus(message: string, status: number): boolean {
    this.spinner.hide();

    switch (status) {
      case 100:
        this.toastr.success(message);
        return true;

      case 0:
        this.toastr.success(message);
        return true;

      case 102:
        this.toastr.error(message);
        return false;

      case 124:
        this.toastr.error(message);
        return false;

      case 114:
        this.toastr.error(message);
        return false;

      default:
        this.toastr.error(message);
        return false;
    }
  }

  /** Plays a sound file from assets. Uses a cached Audio element per source to reduce startup delay. */
  playSound(src: string, options?: { reset?: boolean; volume?: number }): void {
    if (!src) return;

    let audio = this.audioCache.get(src);
    if (!audio) {
      audio = new Audio(src);
      audio.preload = 'auto';
      audio.load();
      this.audioCache.set(src, audio);
    }

    if (options?.volume != null) audio.volume = options.volume;

    if (options?.reset !== false) audio.currentTime = 0;

    audio.play().catch(() => {
      // Browser autoplay policy may block playback until user interaction.
    });
  }
}
